<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020-23 (original work) Open Assessment Technologies SA ;
    import Icon from '../icon/Icon.svelte';

    const sizes = {
        base12: 'base-12',
        base16: 'base-16',
        base24: 'base-24'
    };

    const skins = {
        default: 'default',
        inverted: 'inverted'
    };

    const shapes = {
        square: 'square',
        circular: 'circular',
        pill: 'pill'
    };

    export let id;
    export let label;
    export let ariaLabel = false;
    export let ariaPressed;
    export let ariaExpanded;
    export let ariaHasPopup;
    export let ariaDescribedBy;
    export let ariaControls;
    export let ariaDisabled;
    export let size = sizes.base16;
    export let icon;
    export let iconRtlFlip = false;
    export let skin = skins.default;
    export let shape = shapes.square;
    export let toggled = false;
    export let disabled = false;
    export let visuallyDisabled = false;
    export let dataTestId = null;
    export let showLabelText = false;
    export let tabindex;
</script>

<style>
    /* Inherits actionable styles via the .actionable class & inherited mixins */
    button {
        position: relative;
        color: var(--color-text-default);
        background: none;
        border: none;
        padding: 0;

        @add-mixin inline-flex-center-center;

        &:hover,
        &:focus-visible {
            background: var(--color-bg-actionable-secondary-hover);
        }

        @add-mixin outline-focus 0;

        &.base-12 {
            height: var(--space-5x);
            width: var(--space-5x);
        }

        &.base-16 {
            height: 5.5rem;
            width: 5.5rem;
        }

        &.base-24 {
            height: 6rem;
            width: 6rem;
        }

        &.with-label {
            width: auto;
            padding-left: var(--space-1x5);
            padding-right: var(--space-1x5);
        }

        &:disabled,
        &:disabled:hover,
        &.visually-disabled,
        &.visually-disabled:hover,
        &.visually-disabled:focus {
            background: none;
            border: none;
        }
    }

    /* Toggled */
    button.toggled {
        @add-mixin outline-focus 0.25rem;
        border: var(--border-thin) solid var(--color-border-default);
    }

    /* Inverted */
    :global(.inverted) button {
        color: var(--color-text-inverted);
        background: var(--color-bg-inverted);
        border: none;

        &:hover,
        &:focus-visible {
            background: var(--color-gs-dark-hover-bg);
        }
        &:focus-visible::after {
            border-color: var(--color-border-focus-inverted);
        }
        &:disabled,
        &:disabled:hover,
        &.visually-disabled,
        &.visually-disabled:hover,
        &.visually-disabled:focus {
            border: none;
        }
    }

    /* Inverted & Toggled */
    :global(.inverted) button.toggled {
        border: var(--border-thin) solid var(--color-border-inverted);
        background: var(--color-bg-inverted);

        &:hover {
            background: var(--color-gs-dark-hover-bg);
        }
    }

    /* Circular */
    button.circular {
        border-radius: var(--radius-circular);
        position: relative;
        &:focus-visible:after {
            border-radius: var(--radius-circular);
        }
        &:before {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
        }
    }

    /* Pill */
    button.pill {
        border-radius: var(--radius-pill);
        position: relative;
        &:focus-visible:after {
            border-radius: var(--radius-pill);
        }
        &:before {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
        }
    }

    .label {
        text-transform: uppercase;
        font-size: var(--fontsize-body-xs);
        margin-inline-start: var(--space-1x);
        font-weight: bold;
    }
</style>

<button
    class="icon-bar-btn actionable {size} {skin}"
    class:circular={!showLabelText && [shapes.circular, shapes.pill].includes(shape)}
    class:pill={showLabelText && [shapes.circular, shapes.pill].includes(shape)}
    class:with-label={showLabelText}
    class:visually-disabled={visuallyDisabled}
    class:toggled
    {id}
    title={showLabelText ? void 0 : label}
    data-test-id={dataTestId ? dataTestId : void 0}
    aria-label={ariaLabel || label}
    aria-pressed={ariaPressed}
    aria-expanded={ariaExpanded}
    aria-haspopup={ariaHasPopup}
    aria-controls={ariaControls}
    aria-describedby={ariaDescribedBy || void 0}
    aria-disabled={typeof ariaDisabled !== 'undefined' ? ariaDisabled : visuallyDisabled || void 0}
    tabindex={tabindex || void 0}
    {disabled}
    on:click
    on:keydown
    on:keyup>
    {#if icon}
        <Icon name={icon} ariaHidden={ariaLabel !== false} rtlFlip={iconRtlFlip} />
    {/if}
    {#if showLabelText}
        <span class="label">{label}</span>
    {/if}
    <slot />
</button>
